import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAuth } from '../contexts/AuthContext';
import { Agreement } from '../services/agreementService';
import { docusealService, DocusealSubmission } from '../services/docusealService';

interface DocusealAgreementSignProps {
  agreement: Agreement;
  onSigned: () => void;
  /** Called when DocuSeal cannot serve this agreement, so the HTML flow can take over. */
  onFallback: (reason?: string) => void;
}

const FORM_SCRIPT_SRC = 'https://cdn.docuseal.com/js/form.js';
const POLL_INTERVAL_MS = 4000;

const FORM_CUSTOM_CSS = `
  html, body { margin: 0; background: #fff; }
`.trim();

type ScriptState = 'loading' | 'ready' | 'error';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'docuseal-form': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        'data-src'?: string;
        'data-email'?: string;
        'data-name'?: string;
        'data-expand'?: string;
        'data-minimize'?: string;
        'data-autoscroll-fields'?: string;
        'data-go-to-last'?: string;
        'data-order-as-on-page'?: string;
        'data-with-title'?: string;
        'data-with-decline'?: string;
        'data-send-copy-email'?: string;
        'data-custom-css'?: string;
        'data-completed-message-title'?: string;
        'data-completed-message-body'?: string;
      };
    }
  }
}

const useDocusealScript = (): ScriptState => {
  const [state, setState] = useState<ScriptState>(() =>
    typeof window !== 'undefined' && window.customElements?.get('docuseal-form') ? 'ready' : 'loading',
  );

  useEffect(() => {
    if (state === 'ready') return;

    let script = document.querySelector<HTMLScriptElement>(`script[src="${FORM_SCRIPT_SRC}"]`);
    const handleLoad = () => setState('ready');
    const handleError = () => setState('error');

    if (!script) {
      script = document.createElement('script');
      script.src = FORM_SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
    } else if (window.customElements?.get('docuseal-form')) {
      setState('ready');
      return;
    }

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    return () => {
      script?.removeEventListener('load', handleLoad);
      script?.removeEventListener('error', handleError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
};

export const DocusealAgreementSign: React.FC<DocusealAgreementSignProps> = ({
  agreement,
  onSigned,
  onFallback,
}) => {
  const { user } = useAuth();
  const scriptState = useDocusealScript();
  const [submission, setSubmission] = useState<DocusealSubmission | null>(null);
  const [starting, setStarting] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const formRef = useRef<HTMLElement | null>(null);

  const completedRef = useRef(false);
  const submissionRef = useRef<DocusealSubmission | null>(null);

  useEffect(() => {
    submissionRef.current = submission;
  }, [submission]);

  const checkStatus = useCallback(
    async (showSpinner = false) => {
      const current = submissionRef.current;
      if (!current || completedRef.current) return;

      try {
        if (showSpinner) setFinalizing(true);
        const updated = await docusealService.sync(current.id);
        setSubmission(updated);

        if (updated.is_completed) {
          completedRef.current = true;
          onSigned();
        }
      } catch {
        // Transient failures while DocuSeal finalises the PDF.
      } finally {
        if (showSpinner) setFinalizing(false);
      }
    },
    [onSigned],
  );

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      try {
        setStarting(true);
        const result = await docusealService.start(agreement.id);
        if (cancelled) return;

        if (!result.success || result.fallback_to_html || !result.data?.signing_url) {
          onFallback(result.message);
          return;
        }

        setSubmission(result.data);
        submissionRef.current = result.data;

        if (result.data.is_completed) {
          completedRef.current = true;
          onSigned();
        }
      } catch (err: any) {
        if (cancelled) return;
        onFallback(err?.response?.data?.message || 'Could not start the signing session.');
      } finally {
        if (!cancelled) setStarting(false);
      }
    };

    start();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreement.id]);

  useEffect(() => {
    if (scriptState === 'error') {
      onFallback('The signing form could not be loaded. Showing the standard agreement instead.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptState]);

  useEffect(() => {
    const element = formRef.current;
    if (!element) return;

    const handleCompleted = () => checkStatus(true);
    element.addEventListener('completed', handleCompleted);
    return () => element.removeEventListener('completed', handleCompleted);
  }, [checkStatus, scriptState, submission]);

  useEffect(() => {
    if (!submission || submission.is_completed) return;

    const timer = window.setInterval(() => checkStatus(false), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [submission, checkStatus]);

  const isPreparing = starting || (scriptState === 'loading' && !!submission?.signing_url);

  if (isPreparing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!submission?.signing_url) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {submission?.error_message || 'The signing session is unavailable.'}
        </Alert>
        <Button variant="outlined" onClick={() => onFallback()}>
          Sign the standard agreement instead
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {finalizing && (
        <Box sx={{ px: 3, pt: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Finalising your signed document...
          </Typography>
        </Box>
      )}

      <Box
        className="docuseal-embed-host"
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          bgcolor: '#fff',
          overflowY: 'auto',
          overflowX: 'hidden',
          isolation: 'isolate',
          WebkitOverflowScrolling: 'touch',
          '& docuseal-form': {
            display: 'block',
            width: '100%',
            minHeight: '100%',
          },
          '& iframe': {
            display: 'block',
            width: '100% !important',
            border: '0 !important',
          },
        }}
      >
        <docuseal-form
          key={submission.signing_url}
          ref={formRef as React.RefObject<HTMLElement>}
          data-src={submission.signing_url}
          data-email={user?.email || undefined}
          data-name={user?.name || undefined}
          data-expand="false"
          data-minimize="true"
          data-autoscroll-fields="true"
          data-go-to-last="true"
          data-order-as-on-page="true"
          data-with-title="false"
          data-with-decline="false"
          data-send-copy-email="false"
          data-custom-css={FORM_CUSTOM_CSS}
          data-completed-message-title="Thank you — your agreement is signed"
          data-completed-message-body="You can continue to the platform now."
        />
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}
      >
        <Button variant="outlined" onClick={() => checkStatus(true)} disabled={finalizing}>
          I have signed — continue
        </Button>
        <Button
          component="a"
          href={submission.signing_url}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<OpenInNewIcon />}
        >
          Open in a new tab
        </Button>
        <Typography variant="body2" color="text.secondary">
          Status: {submission.status_label}
        </Typography>
      </Stack>
    </Box>
  );
};
